import { useEffect, useRef } from 'react';
import { burstConfetti } from './confetti';
import styles from './PlacementCelebration.module.scss';

const DURATION = 3600;

export interface PlacementCelebrationPayload {
  id: string;
  candidateName: string;
  jobTitle: string;
  clientName: string;
  salaryFormatted: string;
  salaryLabel: string;
  accountManagerName?: string | null;
  recruiterName?: string | null;
  createdAt: string | Date;
}

interface PlacementCelebrationToastProps {
  payload: PlacementCelebrationPayload;
  onDismiss: (id: string) => void;
}

const PlacementCelebrationToast = ({
  payload,
  onDismiss,
}: PlacementCelebrationToastProps): JSX.Element => {
  const toastRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leavingRef = useRef(false);
  const progressRef = useRef<HTMLSpanElement>(null);
  // Track remaining time so a hover-pause resumes accurately and stays in
  // sync with the CSS progress bar (rather than restarting a full DURATION).
  const remainingRef = useRef(DURATION);
  const startedAtRef = useRef(0);

  const dismiss = () => {
    if (leavingRef.current) return;
    leavingRef.current = true;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const el = toastRef.current;
    if (!el) {
      onDismiss(payload.id);
      return;
    }

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    if (reducedMotion) {
      onDismiss(payload.id);
      return;
    }

    el.classList.add(styles.leaving);
    const handleEnd = () => {
      onDismiss(payload.id);
    };
    el.addEventListener('animationend', handleEnd, { once: true });
    // Fallback in case animationend never fires
    setTimeout(handleEnd, 400);
  };

  const startTimer = () => {
    startedAtRef.current = Date.now();
    timerRef.current = setTimeout(dismiss, remainingRef.current);
  };

  const handleMouseEnter = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      remainingRef.current = Math.max(
        0,
        remainingRef.current - (Date.now() - startedAtRef.current),
      );
    }
    if (progressRef.current) {
      progressRef.current.style.animationPlayState = 'paused';
    }
  };

  const handleMouseLeave = () => {
    if (progressRef.current) {
      progressRef.current.style.animationPlayState = 'running';
    }
    startTimer();
  };

  useEffect(() => {
    startTimer();

    // Fire confetti from the center of the toast
    requestAnimationFrame(() => {
      if (toastRef.current) {
        const r = toastRef.current.getBoundingClientRect();
        burstConfetti(r.left + r.width / 2, r.top + r.height / 2);
      }
    });

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={toastRef}
      className={styles.toast}
      role="status"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.band}>
        <span className={styles.spark}>🎉</span>
        <span className={styles.title}>Placement Made</span>
        <button
          className={styles.closeBtn}
          aria-label="Dismiss"
          onClick={dismiss}
        >
          &times;
        </button>
      </div>

      <div className={styles.body}>
        <div className={styles.placedLine}>
          <span className={styles.cand}>{payload.candidateName}</span> placed as
        </div>
        <div className={styles.placedSub}>
          {payload.jobTitle} <span className={styles.at}>at</span>{' '}
          {payload.clientName}
        </div>

        <div className={styles.salaryRow}>
          <span className={styles.salaryPill}>{payload.salaryFormatted}</span>
          <span className={styles.salaryLabel}>{payload.salaryLabel}</span>
        </div>

        <div className={styles.teamRow}>
          {payload.accountManagerName && (
            <div className={styles.teamCell}>
              <span className={styles.role}>Account Manager</span>
              <span className={styles.who}>{payload.accountManagerName}</span>
            </div>
          )}
          {payload.recruiterName && (
            <div className={styles.teamCell}>
              <span className={styles.role}>Recruiter</span>
              <span className={styles.who}>{payload.recruiterName}</span>
            </div>
          )}
        </div>
      </div>

      <div className={styles.progressTrack}>
        <span
          ref={progressRef}
          className={styles.progressFill}
          style={{ '--dur': `${DURATION}ms` } as React.CSSProperties}
        />
      </div>
    </div>
  );
};

export default PlacementCelebrationToast;
